import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCommunityAuth } from '../../context/CommunityAuthContext';

const CommunityCreateModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useCommunityAuth();
  const isAdmin = user?.role === 'COMMUNITY_ADMIN';

  // When the admin taps "Action", we show a secondary chooser with 4 sub-types:
  // MAD Challenge (existing) + Announcement / Quiz / Poll (new lightweight content).
  const [showActionSub, setShowActionSub] = useState(false);

  const goto = (path) => { onClose(); navigate(path); };

  const options = [
    {
      icon: '+',
      gradient: 'linear-gradient(135deg, #52B8AC 0%, #3A8A80 100%)',
      title: 'Post',
      action: () => goto('/community/create-post'),
    },
    ...(isAdmin ? [{
      icon: '\u26A1',
      gradient: 'linear-gradient(135deg, #FFBB86 0%, #FFA26B 100%)',
      title: 'Action',
      // Open the sub-chooser instead of going straight to MAD challenge create.
      action: () => setShowActionSub(true),
    }] : []),
    ...(isAdmin ? [{
      icon: '\uD83D\uDCC5',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      title: 'Event',
      action: () => goto('/community/create-event'),
    }] : []),
  ];

  const subActions = [
    { key: 'mad', title: 'Challenge', icon: '\u26A1', gradient: 'linear-gradient(135deg, #FFBB86 0%, #FFA26B 100%)',
      action: () => goto('/community/create-challenge'),
      hint: 'Multi-day challenge users join' },
    { key: 'announcement', title: 'Announcement', icon: '\uD83D\uDCE2', gradient: 'linear-gradient(135deg, #4DB6AC 0%, #2E8B7A 100%)',
      action: () => goto('/community/create-action?type=announcement'),
      hint: 'Broadcast a notice' },
    { key: 'quiz', title: 'Quiz', icon: '\u2754', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      action: () => goto('/community/create-action?type=quiz'),
      hint: 'Q&A with correct answers' },
    { key: 'poll', title: 'Poll', icon: '\uD83D\uDCCA', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      action: () => goto('/community/create-action?type=poll'),
      hint: 'Opinion question' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="create-modal-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 12 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        data-testid="create-modal"
        style={{
          background: '#FFFFFF',
          borderRadius: 22,
          padding: '24px 24px 20px',
          maxWidth: 360,
          width: '100%',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18)',
        }}
      >
        <AnimatePresence mode="wait">
          {!showActionSub ? (
            <motion.div key="main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', textAlign: 'center',
                marginBottom: 20,
              }}>
                <h3 data-testid="create-modal-title" style={{
                  fontSize: 16, fontWeight: 700, color: '#1F2937',
                  margin: 0, lineHeight: 1.25, letterSpacing: '-0.3px',
                }}>
                  Create an Impact by Creating
                </h3>
              </div>

              <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
                {options.map((option, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={option.action}
                    data-testid={`create-option-${option.title.toLowerCase()}`}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', gap: 10,
                      padding: '18px 12px',
                      background: '#F8F9FA', border: '1px solid #F0F1F3',
                      borderRadius: 14, cursor: 'pointer', flex: 1,
                    }}
                  >
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: option.gradient,
                      fontSize: 26, color: '#fff', fontWeight: 300,
                    }}>
                      {option.icon}
                    </div>
                    <span style={{
                      fontSize: 13, fontWeight: 600, color: '#2C3E50',
                      textAlign: 'center', lineHeight: 1.2,
                    }}>
                      {option.title}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            // ── Action sub-chooser ──
            <motion.div key="sub" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <button onClick={() => setShowActionSub(false)} data-testid="action-sub-back"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280', fontSize: 18 }}>&larr;</button>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: 0 }}>Choose action type</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {subActions.map(sa => (
                  <motion.button
                    key={sa.key}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={sa.action}
                    data-testid={`action-sub-${sa.key}`}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: '16px 10px',
                      background: '#F8F9FA', border: '1px solid #F0F1F3',
                      borderRadius: 14, cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: sa.gradient, fontSize: 22,
                    }}>
                      {sa.icon}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#2C3E50' }}>{sa.title}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.3 }}>{sa.hint}</div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button onClick={onClose} data-testid="create-modal-cancel"
          style={{
            width: '100%', padding: '12px',
            background: '#F1F3F5', border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 600, color: '#6C757D',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
};

export default CommunityCreateModal;
